import type { ProcessedCourse } from "@/common/course/types";
import type { Program } from "@/common/program/types";
import PromoBanner from "@/components/PromoBanner";
import { PromoBannerVariant } from "@/components/PromoBanner/types";
import {
  SLIM_V2_CTA_HEIGHT,
  SLIM_V2_CTA_WIDTH,
  SLIM_V2_SIDERAIL_CTA_HEIGHT,
  SLIM_V2_SIDERAIL_CTA_WIDTH,
} from "@/components/PromoBanner/variants/Slim/v2/constants";
import { PromoBannerMonarchProps } from "@/monarch/types/PromoBanner";
import { ProgramEnrollButton } from "../templates/components/ProgramEnrollButton";
import { resolveVariant, resolveProps } from "./utils";

type ProgramPromoBannerProps = {
  courses: ProcessedCourse[];
  programData: Program;
  skus: string[];
  data: PromoBannerMonarchProps;
  variant?: PromoBannerVariant;
  /**
   * When true, the slim v2 monarch payload renders as the inverted
   * side-rail layout (`slimV2SideRail`). Set by the page layout when a
   * side-rail column is shown.
   */
  sideRail?: boolean;
};

export default function ProgramPromoBanner({
  courses,
  programData,
  skus,
  data,
  variant = "slim",
  sideRail = false,
}: ProgramPromoBannerProps) {
  const resolvedVariant = resolveVariant(data, variant, { sideRail });

  if (!resolvedVariant) {
    return null;
  }

  const variantProps = resolveProps(data);
  // Pick the right CTA sizing for each slim v2 layout. Standard slim v2
  // uses a custom tablet breakpoint (906px); the side-rail variant uses
  // the default `md` (768px). Non-slim-v2 variants fall back to a plain
  // responsive width.
  const isSlimV2 = resolvedVariant === "slimV2";
  const isSlimV2SideRail = resolvedVariant === "slimV2SideRail";

  const widthClass = isSlimV2
    ? SLIM_V2_CTA_WIDTH
    : isSlimV2SideRail
      ? SLIM_V2_SIDERAIL_CTA_WIDTH
      : "w-full md:w-auto";

  const slimV2HeightClasses = isSlimV2
    ? `!mx-0 ${SLIM_V2_CTA_HEIGHT} py-0 whitespace-nowrap`
    : isSlimV2SideRail
      ? `!mx-0 ${SLIM_V2_SIDERAIL_CTA_HEIGHT} py-0 whitespace-nowrap`
      : "";

  const buttonClass = `${widthClass} ${
    resolvedVariant === "slim" ? "mb-4" : ""
  } ${slimV2HeightClasses}`;

  return (
    <PromoBanner
      {...variantProps}
      variant={resolvedVariant}
      ctaElement={
        <ProgramEnrollButton
          courses={courses}
          programData={programData}
          skus={skus}
          buttonClass={buttonClass}
          enableTracking={true}
        />
      }
    />
  );
}
